import { Company } from '@app/models/Company'
import { EmailAddress } from '@app/models/EmailAddress'
import { PhoneNumber } from '@app/models/PhoneNumber'
import { IQuickContact, QuickContact } from '@app/models/QuickContact'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, take, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

export type OnSaveCallbackFunc = (contact: IQuickContact) => void

// -------------------------
// STATE
// -------------------------
export interface IQuickAddState {
  readonly error?: string
  readonly forceIsCompany: boolean
  readonly isOpen: boolean
  readonly isSaving: boolean
  readonly contact: IQuickContact
  readonly sourceId: string
}

const QuickAddStateRecord = Record({
  contact: new QuickContact(),
  error: undefined,
  forceIsCompany: false,
  isOpen: false,
  isSaving: false,
  sourceId: '',
})

export class QuickAddState extends QuickAddStateRecord
  implements IQuickAddState {
  public static fromJS(initialState?: Partial<IQuickAddState>) {
    if (!initialState) {
      return new QuickAddState()
    }

    const { contact, ...rest } = initialState

    return new QuickAddState({
      contact: QuickContact.fromJS(contact),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly forceIsCompany: boolean
  public readonly isOpen: boolean
  public readonly isSaving: boolean
  public readonly contact: QuickContact
  public readonly sourceId: string

  constructor(props?: Partial<QuickAddState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------
export enum ActionTypes {
  CloseQuickAdd = 'QUICK_ADD::CLOSE',
  ResetState = 'QUICK_ADD::RESET_STATE',
  SaveQuickAdd = 'QUICK_ADD::SAVE_QUICK_ADD',
  SaveQuickAddFailure = 'QUICK_ADD::SAVE_FAILURE',
  SaveQuickAddRequest = 'QUICK_ADD::SAVE_REQUEST',
  SaveQuickAddSuccess = 'QUICK_ADD::SAVE_SUCCESS',
  SetCompany = 'QUICK_ADD::SET_COMPANY',
  SetEmailAddresses = 'QUICK_ADD::SET_EMAIL_ADDRESSES',
  SetFirstName = 'QUICK_ADD::SET_FIRST_NAME',
  SetIsCompany = 'QUICK_ADD::SET_IS_COMPANY',
  SetLastName = 'QUICK_ADD::SET_LAST_NAME',
  SetPhoneNumbers = 'QUICK_ADD::SET_PHONE_NUMBERS',
  ShowQuickAdd = 'QUICK_ADD::SHOW',
}

interface ICloseQuickAddAction {
  type: ActionTypes.CloseQuickAdd
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveQuickAddAction {
  contact: IQuickContact
  type: ActionTypes.SaveQuickAdd
}

interface ISaveQuickAddFailureAction {
  error: string
  type: ActionTypes.SaveQuickAddFailure
}

interface ISaveQuickAddRequestAction {
  type: ActionTypes.SaveQuickAddRequest
}

interface ISaveQuickAddSuccessAction {
  contactId: string
  type: ActionTypes.SaveQuickAddSuccess
}

interface ISetCompanyAction {
  company?: Company
  type: ActionTypes.SetCompany
}

interface ISetEmailAddressesAction {
  emailAddresses: List<EmailAddress>
  type: ActionTypes.SetEmailAddresses
}

interface ISetFirstNameAction {
  firstName: string
  type: ActionTypes.SetFirstName
}

interface ISetIsCompanyAction {
  isCompany: boolean
  type: ActionTypes.SetIsCompany
}

interface ISetLastNameAction {
  lastName: string
  type: ActionTypes.SetLastName
}

interface ISetPhoneNumbersAction {
  phoneNumbers: List<PhoneNumber>
  type: ActionTypes.SetPhoneNumbers
}

interface IShowQuickAddAction {
  name: string
  onSave: OnSaveCallbackFunc
  sourceId: string
  type: ActionTypes.ShowQuickAdd
  isCompany?: boolean
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICloseQuickAddAction
  | IResetStateAction
  | ISaveQuickAddFailureAction
  | ISaveQuickAddRequestAction
  | ISaveQuickAddSuccessAction
  | ISetCompanyAction
  | ISetEmailAddressesAction
  | ISetFirstNameAction
  | ISetIsCompanyAction
  | ISetLastNameAction
  | ISetPhoneNumbersAction
  | IShowQuickAddAction

// -------------------------
// ACTION CREATORS
// -------------------------
function closeQuickAdd(): ICloseQuickAddAction {
  return { type: ActionTypes.CloseQuickAdd }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveQuickAdd(contact: IQuickContact): ISaveQuickAddAction {
  return {
    contact,
    type: ActionTypes.SaveQuickAdd,
  }
}

function saveQuickAddFailure(error: string): ISaveQuickAddFailureAction {
  return {
    error,
    type: ActionTypes.SaveQuickAddFailure,
  }
}

function saveQuickAddRequest(): ISaveQuickAddRequestAction {
  return { type: ActionTypes.SaveQuickAddRequest }
}

function saveQuickAddSuccess(contactId: string): ISaveQuickAddSuccessAction {
  return {
    contactId,
    type: ActionTypes.SaveQuickAddSuccess,
  }
}

function setCompany(company?: Company): ISetCompanyAction {
  return {
    company,
    type: ActionTypes.SetCompany,
  }
}

function setEmailAddresses(
  emailAddresses: List<EmailAddress>,
): ISetEmailAddressesAction {
  return {
    emailAddresses,
    type: ActionTypes.SetEmailAddresses,
  }
}

function setFirstName(firstName: string): ISetFirstNameAction {
  return {
    firstName,
    type: ActionTypes.SetFirstName,
  }
}

function setIsCompany(isCompany: boolean): ISetIsCompanyAction {
  return {
    isCompany,
    type: ActionTypes.SetIsCompany,
  }
}

function setLastName(lastName: string): ISetLastNameAction {
  return {
    lastName,
    type: ActionTypes.SetLastName,
  }
}

function setPhoneNumbers(
  phoneNumbers: List<PhoneNumber>,
): ISetPhoneNumbersAction {
  return {
    phoneNumbers,
    type: ActionTypes.SetPhoneNumbers,
  }
}

function showQuickAdd(
  sourceId: string,
  name: string,
  onSave: OnSaveCallbackFunc,
  isCompany?: boolean,
): IShowQuickAddAction {
  return {
    isCompany,
    name,
    onSave,
    sourceId,
    type: ActionTypes.ShowQuickAdd,
  }
}

export const actionCreators = {
  closeQuickAdd,
  resetState,
  saveQuickAdd,
  setCompany,
  setEmailAddresses,
  setFirstName,
  setIsCompany,
  setLastName,
  setPhoneNumbers,
}

export const quickAddActionCreators = {
  showQuickAdd,
}

// -------------------------
// REDUCER
// -------------------------
export const reducer: Reducer<QuickAddState> = (
  state: QuickAddState = new QuickAddState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.CloseQuickAdd:
      return state.set('isOpen', false) as QuickAddState

    case ActionTypes.ResetState:
      return new QuickAddState()

    case ActionTypes.SaveQuickAddFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as QuickAddState

    case ActionTypes.SaveQuickAddRequest:
      return state.set('isSaving', true) as QuickAddState

    case ActionTypes.SaveQuickAddSuccess:
      return state
        .setIn(['contact', 'contactId'], action.contactId)
        .set('isSaving', false) as QuickAddState

    case ActionTypes.SetCompany: {
      const newContact = state.contact.set(
        'company',
        action.company,
      ) as QuickContact
      return state.set('contact', newContact.validate()) as QuickAddState
    }

    case ActionTypes.SetEmailAddresses: {
      const newContact = state.contact.set(
        'emailAddresses',
        action.emailAddresses,
      ) as QuickContact
      return state.set('contact', newContact.validate()) as QuickAddState
    }

    case ActionTypes.SetFirstName: {
      const newContact = state.contact.set(
        'firstName',
        action.firstName,
      ) as QuickContact
      return state.set('contact', newContact.validate()) as QuickAddState
    }

    case ActionTypes.SetIsCompany: {
      let firstName = ''
      let lastName = ''

      const fullName = `${state.contact.firstName} ${state.contact.lastName}`.trim()

      if (action.isCompany === true) {
        lastName = fullName
        firstName = ''
      } else {
        const index = fullName.lastIndexOf(' ')
        if (index > 0) {
          firstName = fullName.substring(0, index)
          lastName = fullName.substring(index + 1)
        } else {
          firstName = fullName
        }
      }

      const newContact = state.contact
        .set('firstName', firstName)
        .set('isCompany', action.isCompany)
        .set('lastName', lastName) as QuickContact
      return state.set('contact', newContact.validate()) as QuickAddState
    }

    case ActionTypes.SetLastName: {
      const newContact = state.contact.set(
        'lastName',
        action.lastName,
      ) as QuickContact
      return state.set('contact', newContact.validate()) as QuickAddState
    }

    case ActionTypes.SetPhoneNumbers: {
      const newContact = state.contact.set(
        'phoneNumbers',
        action.phoneNumbers,
      ) as QuickContact
      return state.set('contact', newContact.validate()) as QuickAddState
    }

    case ActionTypes.ShowQuickAdd: {
      let firstName = ''
      let lastName = ''
      if (action.isCompany === true) {
        lastName = action.name
      } else {
        const index = action.name?.trim().lastIndexOf(' ')
        if (index > 0) {
          firstName = action.name.substring(0, index)
          lastName = action.name.substring(index + 1)
        } else {
          firstName = action.name
        }
      }

      const newContact = new QuickContact({
        firstName,
        isCompany: action.isCompany || false,
        lastName,
      })

      return state
        .set('contact', newContact.validate())
        .set('forceIsCompany', action.isCompany !== undefined)
        .set('isOpen', true)
        .set('sourceId', action.sourceId) as QuickAddState
    }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* saveQuickAddSaga(action: ISaveQuickAddAction) {
  try {
    yield put(saveQuickAddRequest())

    const reqUrl = action.contact.isCompany
      ? '/api/Companies/Create'
      : '/api/Contacts'

    const reqOpts = {
      body: JSON.stringify({
        company: action.contact.company,
        emailAddresses: action.contact.emailAddresses.toJS(),
        firstName: action.contact.firstName,
        lastName: action.contact.lastName,
        phoneNumbers: action.contact.phoneNumbers.toJS(),
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      yield put(saveQuickAddSuccess(result.entity.id))
    } else {
      yield put(saveQuickAddFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveQuickAddFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* showQuickAddSaga(action: IShowQuickAddAction) {
  try {
    // Wait for the quick add to either be saved or the window cancelled
    const nextAction = yield take([
      ActionTypes.SaveQuickAddSuccess,
      ActionTypes.CloseQuickAdd,
    ])

    if (nextAction.type === ActionTypes.SaveQuickAddSuccess) {
      const mState: QuickAddState = yield select(selectModuleState())
      // This should really always be true but just making sure the quick add is for the same editor id as the original action
      if (mState.sourceId === action.sourceId) {
        action.onSave(mState.contact)
      }
      // Ensure that the quick add modal is closed
      yield put(closeQuickAdd())
    }
  } catch (err) {
    yield errorToast('Oops!', err.message)
  }
}

function* watchSaveQuickAdd() {
  yield takeLatest(ActionTypes.SaveQuickAdd, saveQuickAddSaga)
}

function* watchShowQuickAdd() {
  yield takeLatest(ActionTypes.ShowQuickAdd, showQuickAddSaga)
}

export function* quickAddRootSaga() {
  yield fork(watchSaveQuickAdd)
  yield fork(watchShowQuickAdd)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): QuickAddState =>
  state.quickAdd

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectForceIsCompany = () =>
  createSelector(selectModuleState(), (mState) => mState.forceIsCompany)

export const selectIsOpen = () =>
  createSelector(selectModuleState(), (mState) => mState.isOpen)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectContact = () =>
  createSelector(selectModuleState(), (mState) => mState.contact)

export const selectSourceId = () =>
  createSelector(selectModuleState(), (mState) => mState.sourceId)
